/**
 * 自動配信管理画面（完全版）
 * 作成日: 2025年10月1日
 * 更新日: 2025年10月1日
 * 機能: 自動配信の有効/無効切り替え、設定編集、実行履歴確認
 */

import React, { useState, useEffect } from 'react';
import { Calendar, Settings, Play, Pause, CheckCircle, XCircle, Clock, TrendingUp, AlertCircle, Edit2, X, Save } from 'lucide-react';

interface DistributionConfig {
    coupon_name: string;
    discount_value: number;
    discount_type: string;
    expires_days: number;
    description: string;
}

interface DistributionSetting {
    id: number;
    type: string;
    name: string;
    description: string;
    enabled: boolean;
    schedule: string;
    config: DistributionConfig;
    last_run_at: string | null;
    next_run_at: string | null;
    run_count: number;
    success_count: number;
    error_count: number;
    last_error: string | null;
    updated_at: string;
}

interface DistributionLog {
    id: number;
    setting_id: number;
    type: string;
    status: string;
    target_count: number;
    success_count: number;
    error_count: number;
    details: string;
    error_message: string | null;
    executed_at: string;
    setting_name: string;
}

const AutoDistributionManager: React.FC = () => {
    const [settings, setSettings] = useState<DistributionSetting[]>([]);
    const [logs, setLogs] = useState<DistributionLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [executing, setExecuting] = useState<string | null>(null);
    const [selectedSetting, setSelectedSetting] = useState<DistributionSetting | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editForm, setEditForm] = useState<DistributionConfig>({
        coupon_name: '',
        discount_value: 0,
        discount_type: 'fixed',
        expires_days: 7,
        description: ''
    });

    // 設定一覧を取得
    const fetchSettings = async () => {
        try {
            const response = await fetch('/api/auto-distribution/settings');
            const data = await response.json();
            if (data.success) {
                setSettings(data.settings);
            }
        } catch (error) {
            console.error('設定取得エラー:', error);
        }
    };

    // ログ一覧を取得
    const fetchLogs = async () => {
        try {
            const response = await fetch('/api/auto-distribution/logs?limit=20');
            const data = await response.json();
            if (data.success) {
                setLogs(data.logs);
            }
        } catch (error) {
            console.error('ログ取得エラー:', error);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchSettings(), fetchLogs()]);
            setLoading(false);
        };
        loadData();
    }, []);

    // 有効/無効を切り替え
    const toggleEnabled = async (id: number, currentEnabled: boolean) => {
        try {
            const response = await fetch(`/api/auto-distribution/settings/${id}/toggle`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: !currentEnabled })
            });

            const data = await response.json();
            if (data.success) {
                await fetchSettings();
                alert(data.message);
            }
        } catch (error) {
            console.error('切り替えエラー:', error);
            alert('設定の切り替えに失敗しました');
        }
    };

    // 手動実行
    const executeManually = async (type: string) => {
        if (!confirm('この配信を手動で実行しますか？')) return;

        setExecuting(type);
        try {
            const response = await fetch(`/api/auto-distribution/execute/${type}`, {
                method: 'POST'
            });

            const data = await response.json();
            if (data.success) {
                alert(`配信完了: ${data.result.count || 0}件`);
                await Promise.all([fetchSettings(), fetchLogs()]);
            } else {
                alert(`配信失敗: ${data.error}`);
            }
        } catch (error) {
            console.error('実行エラー:', error);
            alert('配信実行に失敗しました');
        } finally {
            setExecuting(null);
        }
    };

    // 編集モーダルを開く
    const openEditModal = (setting: DistributionSetting) => {
        setSelectedSetting(setting);
        setEditForm(setting.config);
        setShowEditModal(true);
    };

    // 設定を保存
    const saveSettings = async () => {
        if (!selectedSetting) return;

        try {
            const response = await fetch(`/api/auto-distribution/settings/${selectedSetting.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ config: editForm })
            });

            const data = await response.json();
            if (data.success) {
                alert('設定を保存しました');
                await fetchSettings();
                setShowEditModal(false);
            } else {
                alert(`保存失敗: ${data.error}`);
            }
        } catch (error) {
            console.error('保存エラー:', error);
            alert('設定の保存に失敗しました');
        }
    };

    // Cronスケジュール表記を日本語に変換
    const formatSchedule = (schedule: string): string => {
        const scheduleMap: { [key: string]: string } = {
            '0 9 * * 3': '毎週水曜 9:00',
            '0 9 * * 5': '毎週金曜 9:00',
            '0 0 1 * *': '毎月1日 0:00'
        };
        return scheduleMap[schedule] || schedule;
    };

    // 配信タイプのアイコン
    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'mens_day':
                return '🎩';
            case 'ladies_day':
                return '👗';
            case 'birthday':
                return '🎂';
            default:
                return '📬';
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-gray-500">読み込み中...</div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">自動配信管理</h1>
                <p className="text-gray-600">クーポン・特典の自動配信設定を管理します</p>
            </div>

            {/* 設定一覧 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {settings.map((setting) => (
                    <div
                        key={setting.id}
                        className={`bg-white rounded-lg shadow-md p-6 border-l-4 ${
                            setting.enabled ? 'border-green-500' : 'border-gray-400'
                        }`}
                    >
                        {/* ヘッダー */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center">
                                <span className="text-3xl mr-3">{getTypeIcon(setting.type)}</span>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">{setting.name}</h3>
                                    <p className="text-sm text-gray-500">{setting.description}</p>
                                </div>
                            </div>
                            <button
                                onClick={() => openEditModal(setting)}
                                className="text-gray-400 hover:text-blue-600"
                                title="設定を編集"
                            >
                                <Settings className="w-5 h-5" />
                            </button>
                        </div>

                        {/* クーポン詳細 */}
                        <div className="bg-gray-50 rounded p-3 mb-3">
                            <div className="text-xs text-gray-500 mb-1">クーポン内容</div>
                            <div className="text-sm font-medium text-gray-800">{setting.config.coupon_name}</div>
                            <div className="text-sm text-gray-600 mt-1">
                                {setting.config.discount_value}円OFF・{setting.config.expires_days}日間有効
                            </div>
                        </div>

                        {/* スケジュール */}
                        <div className="flex items-center text-sm text-gray-600 mb-3">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>{formatSchedule(setting.schedule)}</span>
                        </div>

                        {/* 統計情報 */}
                        <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                            <div className="bg-blue-50 rounded p-2">
                                <div className="text-xs text-blue-600">実行回数</div>
                                <div className="text-lg font-bold text-blue-700">{setting.run_count}</div>
                            </div>
                            <div className="bg-green-50 rounded p-2">
                                <div className="text-xs text-green-600">成功</div>
                                <div className="text-lg font-bold text-green-700">{setting.success_count}</div>
                            </div>
                            <div className="bg-red-50 rounded p-2">
                                <div className="text-xs text-red-600">失敗</div>
                                <div className="text-lg font-bold text-red-700">{setting.error_count}</div>
                            </div>
                        </div>

                        {/* 最終実行時刻 */}
                        {setting.last_run_at && (
                            <div className="flex items-center text-xs text-gray-500 mb-3">
                                <Clock className="w-3 h-3 mr-1" />
                                <span>前回: {new Date(setting.last_run_at).toLocaleString('ja-JP')}</span>
                            </div>
                        )}

                        {/* エラーメッセージ */}
                        {setting.last_error && (
                            <div className="bg-red-50 border border-red-200 rounded p-2 mb-3">
                                <div className="flex items-center text-xs text-red-700">
                                    <AlertCircle className="w-3 h-3 mr-1" />
                                    <span className="font-semibold">エラー:</span>
                                </div>
                                <p className="text-xs text-red-600 mt-1">{setting.last_error}</p>
                            </div>
                        )}

                        {/* アクションボタン */}
                        <div className="flex gap-2">
                            <button
                                onClick={() => toggleEnabled(setting.id, setting.enabled)}
                                className={`flex-1 flex items-center justify-center py-2 px-3 rounded text-sm font-medium ${
                                    setting.enabled
                                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        : 'bg-green-500 text-white hover:bg-green-600'
                                }`}
                            >
                                {setting.enabled ? (
                                    <>
                                        <Pause className="w-4 h-4 mr-1" />
                                        無効化
                                    </>
                                ) : (
                                    <>
                                        <Play className="w-4 h-4 mr-1" />
                                        有効化
                                    </>
                                )}
                            </button>
                            <button
                                onClick={() => executeManually(setting.type)}
                                disabled={!setting.enabled || executing === setting.type}
                                className="flex-1 flex items-center justify-center py-2 px-3 rounded text-sm font-medium bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                            >
                                {executing === setting.type ? (
                                    <>実行中...</>
                                ) : (
                                    <>
                                        <Play className="w-4 h-4 mr-1" />
                                        手動実行
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* 実行履歴 */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2" />
                    実行履歴
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b">
                                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">実行日時</th>
                                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">配信名</th>
                                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">ステータス</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">対象数</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">成功</th>
                                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">失敗</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center py-8 text-gray-500">
                                        実行履歴がありません
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log) => (
                                    <tr key={log.id} className="border-b hover:bg-gray-50">
                                        <td className="py-3 px-4 text-sm text-gray-600">
                                            {new Date(log.executed_at).toLocaleString('ja-JP')}
                                        </td>
                                        <td className="py-3 px-4 text-sm">
                                            <div className="flex items-center">
                                                <span className="mr-2">{getTypeIcon(log.type)}</span>
                                                <span className="font-medium text-gray-800">{log.setting_name}</span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                            {log.status === 'success' ? (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    成功
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    失敗
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-gray-600 text-right">{log.target_count}</td>
                                        <td className="py-3 px-4 text-sm text-green-600 text-right font-medium">{log.success_count}</td>
                                        <td className="py-3 px-4 text-sm text-red-600 text-right font-medium">{log.error_count}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 編集モーダル */}
            {showEditModal && selectedSetting && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold text-gray-800 flex items-center">
                                <Edit2 className="w-5 h-5 mr-2" />
                                {selectedSetting.name} - 設定編集
                            </h3>
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* クーポン名 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    クーポン名
                                </label>
                                <input
                                    type="text"
                                    value={editForm.coupon_name}
                                    onChange={(e) => setEditForm({ ...editForm, coupon_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* 割引金額 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    割引金額（円）
                                </label>
                                <input
                                    type="number"
                                    value={editForm.discount_value}
                                    onChange={(e) => setEditForm({ ...editForm, discount_value: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    min="0"
                                    step="100"
                                />
                            </div>

                            {/* 有効期限 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    有効期限（日数）
                                </label>
                                <input
                                    type="number"
                                    value={editForm.expires_days}
                                    onChange={(e) => setEditForm({ ...editForm, expires_days: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    min="1"
                                    max="365"
                                />
                            </div>

                            {/* 説明文 */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    説明文
                                </label>
                                <input
                                    type="text"
                                    value={editForm.description}
                                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* アクションボタン */}
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowEditModal(false)}
                                className="flex-1 py-2 px-4 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={saveSettings}
                                className="flex-1 py-2 px-4 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center"
                            >
                                <Save className="w-4 h-4 mr-2" />
                                保存
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoDistributionManager;
